import StorageManager from './StorageManager';
import { validateEvent, createEventBatches } from '../utils/dataProcessor';
import { store } from '../../../app/store/store';
import { sendAnalyticsEvents } from '../../../app/store/slices/analyticsSlice';

class EventBatcher {
  constructor() {
    this.events = [];
    this.batchSize = 20;
    this.flushInterval = 30000; // 30 seconds
    this.maxRetries = 3;
    this.retryDelay = 1000;
    this.isOnline = navigator.onLine;
    this.storage = new StorageManager();
    this.pendingBatches = new Map();
    this.flushTimer = null;
    this.apiEnabled = true;
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
  }

  addEvent(event) {
    if (!validateEvent(event)) {
      console.warn('Invalid event discarded:', event);
      return false;
    }

    this.events.push(event);
    
    if (this.events.length >= this.batchSize) {
      this.flush();
    }
    
    return true;
  }

  start() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    
    this.flushTimer = setInterval(() => this.flush(), this.flushInterval);
    
    if (this.isOnline && this.apiEnabled) {
      this.flushStoredEvents();
    }
  }

  
  stop() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush(true);
  }

  /**
   * Flush current batch
   */
  async flush(isSync = false) {
    if (this.events.length === 0) return;

    const eventsToSend = [...this.events];
    this.events = [];

    if (!this.isOnline) {
      this.storage.storeEvents(eventsToSend);
      return;
    }

    if (!this.apiEnabled) {
      return;
    }

    try {
      await this.sendEventBatch(eventsToSend, isSync);
    } catch (error) {
      console.error('Failed to send analytics batch:', error);
    }
  }

  /**
   * Send a batch of events with retry logic using Redux
   */
  async sendEventBatch(events, isSync = false, retryCount = 0) {
    const batchId = this.generateBatchId();
    
    try {
      const batches = createEventBatches(events, this.batchSize);
      
      for (const batch of batches) {
        if (isSync) {
          this.storage.storeEvents(batch);
          await this.sendEventsViaRedux(batch);
        }
      }
    } catch (error) {
      if (retryCount < this.maxRetries && !error.message?.includes('404')) {
        const delay = this.retryDelay * Math.pow(2, retryCount); 
        setTimeout(() => {
          this.sendEventBatch(events, isSync, retryCount + 1);
        }, delay);
      } else {
        console.error(`Failed to send batch ${batchId} after ${this.maxRetries} attempts`);
        throw error;
      }
    }
  }

  /**
   * Send events using Redux thunk (handles JWT automatically)
   */
  async sendEventsViaRedux(events) {
    try {
      const result = await store.dispatch(sendAnalyticsEvents(events));
      if (sendAnalyticsEvents.rejected.match(result)) {
        throw new Error(result.payload || 'Failed to send events via Redux');
      }
      
      return result.payload;
    } catch (error) {
      console.error('Redux analytics send failed:', error);
      throw error;
    }
  }

  /**
   * Flush events stored offline
   */
  async flushStoredEvents() {
    if (!this.apiEnabled) return;
    
    try {
      const storedEvents = this.storage.getStoredEvents();
      
      if (storedEvents.length === 0) return;
      
      console.debug(`Flushing ${storedEvents.length} stored events`);
      
      // Send stored events via Redux
      await this.sendEventsViaRedux(storedEvents);
 
      this.storage.clearStoredEvents();
      
    } catch (error) {
      console.error('Failed to flush stored events:', error);
      // Don't clear stored events if sending failed
    }
  }

  /**
   * Handle online event
   */
  handleOnline() {
    this.isOnline = true; 
    if (this.apiEnabled) {
      setTimeout(() => {
        this.flushStoredEvents();
      }, 1000);
    }
  }

  /**
   * Handle offline event
   */
  handleOffline() {
    this.isOnline = false;
  }

  /**
   * Generate unique batch ID
   */
  generateBatchId() {
    return `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current batch status
   */
  getStatus() {
    return {
      isOnline: this.isOnline,
      apiEnabled: this.apiEnabled,
      pendingEvents: this.events.length,
      storedEvents: this.storage.getStoredEvents().length,
      pendingBatches: this.pendingBatches.size,
      isRunning: this.flushTimer !== null
    };
  }

  /**
   * Force flush all pending events
   */
  async forceFlush() {
    await this.flush(false); 
    if (this.isOnline && this.apiEnabled) {
      await this.flushStoredEvents();
    }
  }

  /**
   * Enable/disable API calls
   */
  setApiEnabled(enabled) {
    this.apiEnabled = enabled;
    console.debug(`Analytics API ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Check if we have authentication (user is logged in)
   */
  hasAuthentication() {
    try {
      const authData = JSON.parse(localStorage.getItem('authData') || '{}');
      return !!authData.accessToken;
    } catch (error) {
      return false;
    }
  }

}

export default EventBatcher;